import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { CONFIRMATION_TYPES } from '../../enums/confirmation-types.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { isDefined } from '../../utilities/is-defined';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { TimeUtilities } from '../../utilities/time-utilities';
import { MaterialModel } from '../low-level/material.model';
import { CONSUME_CODE } from '../low-level/material.enum';
import { NotificationModel } from '../low-level/notification.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DurationModelOptions } from '../options/duration-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { DurationModel, JsonDurationModel } from '../primitive/duration.model';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { ODataTextOutModel, TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { TreeViewModel } from '../primitive/tree-view.model';
import { ArrayModel } from './array.model';
import { CodeModel } from './code.model';
import { CompositeModel } from './composite.model';
import { EquipmentStatus, JsonConfirmationEquipmentModel } from './confirmation-equipment.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';
import { DropdownModel } from './dropdown.model';
import { RadioModel } from './radio.model';
import { DropdownModelOptions } from '../options/dropdown-model-options';
import { cloneDeep } from 'lodash';
import {
    AppointmentSlot,
    ODataAppointmentSlotsPayload,
    OdataSlotIntervalModel,
} from '../../services/appointment-booking/appointment-booking-model';

export class ConfirmationFollowupModel extends CompositeModel {
    protected clientManager: ClientManagerInterface;
    private readonly configService: ConfigService;

    public followupOptions: RadioModel<TextModel>;
    public appointmentDate: DateModel;
    public appointmentTime: TimeModel;
    public followUpUMRCodes: DropdownModel<TextModel> | undefined;
    public notificationType: ConfigConstants.NotificationTypes;
    public additionalTime: DurationModel;
    public shortCrmFollowupDateTime: moment.Moment;
    public isForced: boolean;
    public followUpReasons: DropdownModel<TextModel> | undefined;
    public followupTextLong: MultilineTextModel;
    public followupTextShort: MultilineTextModel;
    public additionalInfo: MultilineTextModel;
    public crmLongTextTRTESet: MultilineTextModel | undefined;
    public shortCrmFollowupOptions: string | undefined;
    public shortCrmAppointmentTime: moment.Moment | undefined;
    public normalCrmFollowupOptions: string;
    public normalCrmAppointmentTime: moment.Moment;
    public followUpText: string[] = [];
    public readonly assayIndicator = ConfigConstants.assayIndicator;
    public reasonsCode: TreeViewModel<CodeModel> | undefined;
    public reasonsCodeFallback: DropdownModel<TextModel>;
    public assayNumber: MultilineTextModel;
    public lotNumber: MultilineTextModel;
    public showLegalCheckHandover = false;
    public notificationLanguage: string;
    public legalCheckHandoverUserDocumentationText: string;
    public legalCheckHandoverSafetyInformationText: string;
    public legalCheckHandoverUserDocumentation: RadioModel<TextModel>;
    public legalCheckHandoverSafetyInformation: RadioModel<TextModel>;
    public appointmentSlots: AppointmentSlot[] = [];
    public availableSlotsDropdownModel: DropdownModel<TextModel>;
    private nextWorkingDayDateTime: moment.Moment;
    public enableAppointmentBooking: boolean;

    /**
     * Prepare a model for the Confirmation Followup Model.
     *
     * @param notification
     * @param notificationType
     * @param confirmationType
     * @param isMaterialRequestDependent
     * @param equipmentStatus
     * @param materialsList
     * @class
     */
    public constructor(
        public notification: NotificationModel,
        notificationType: ConfigConstants.NotificationTypes,
        public confirmationType: CONFIRMATION_TYPES,
        public isMaterialRequestDependent: boolean,
        public equipmentStatus: EquipmentStatus | undefined,
        public materialsList: MaterialModel[] | undefined,
    ) {
        super('ConfirmationFollowupModel');
        const injector = CustomInjector.getInjector();
        this.configService = injector.get(ConfigService);
        const valueHelperService = injector.get(ValueHelperService);
        const translate = injector.get(TranslateService);
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.clientManager = injector.get(ClientManagerInstanceToken);
        const notificationUtilities = injector.get(NotificationUtilities);

        this.notificationType = notificationType;
        const followUpReasons = valueHelperService.getFollowupReason();
        if (followUpReasons && followUpReasons.length > 0) {
            const followUpReasonsEntries = followUpReasons.map(
                (vh: ValueHelperModel) =>
                    new DisplayDecorator<TextModel>(vh.descText, new TextModel('FollowupReason', vh.descText)),
            );

            this.followUpReasons = <DropdownModel<TextModel>>(
                this.set(new DropdownModel<TextModel>('FollowupReason', followUpReasonsEntries))
            );
            modelOptionsLoader.load(this.followUpReasons, 'ConfirmationFollowup', 'FollowupReason');
            const selectDefaultFollowupReason = this.configService.getSelectDefaultFollowupReason();
            if (selectDefaultFollowupReason && !this.clientManager.isNgmSA()) {
                // sele ct the first entry
                this.followUpReasons.selectByIndex(0);
            }
        }

        // show followup UMR codes only for notification type MU
        if (this.notificationType === ConfigConstants.NotificationTypes.Update) {
            const followUpUMRCodes = valueHelperService.getFollowUpUMRCode();

            const followUpUMRCodesEntries = followUpUMRCodes.map(
                (vh: ValueHelperModel) =>
                    new DisplayDecorator<TextModel>(vh.descText, new TextModel('UmrCode', vh.code)),
            );

            this.followUpUMRCodes = <DropdownModel<TextModel>>(
                this.set(new DropdownModel<TextModel>('UmrCode', followUpUMRCodesEntries))
            );
            modelOptionsLoader.load(this.followUpUMRCodes, 'ConfirmationFollowup', 'UMRCode');
        }

        const optionGroup: Array<DisplayDecorator<TextModel>> = [];
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        optionGroup.push(
            new DisplayDecorator(
                <string>translate.instant('CONFIRMATION_FOLLOWUP_OPTION_NA'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
            ),
        );

        this.followupOptions = <RadioModel<TextModel>>(
            this.set(
                new RadioModel<TextModel>(
                    'FollowUpVisitFlag',
                    optionGroup,
                    new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
                ),
            )
        );

        this.updateNextWorkingDayDateTime();
        this.appointmentTime = <TimeModel>(
            this.set(
                new TimeModel(
                    'FollowUpVisitTime',
                    this.nextWorkingDayDateTime,
                    new TimeModelOptions({ minValue: moment().startOf('minutes') }),
                ),
            )
        );
        this.appointmentDate = <DateModel>this.set(new DateModel('FollowUpVisitDate', this.appointmentTime.value));

        const followupDefaultAdditionalTime = this.configService.getFollowupDefaultAdditionalTime();
        const followupMinAdditionalTime = this.configService.getFollowupMinAdditionalTime();
        const followupAdditionalTimeStep = this.configService.getFollowupAdditionalTimeStep();

        this.additionalTime = <DurationModel>this.set(
            new DurationModel(
                'FollowUpVisitAddTime',
                moment.duration(followupDefaultAdditionalTime, 'minutes'),
                new DurationModelOptions({
                    step: followupAdditionalTimeStep,
                    unit: <moment.unitOfTime.Base>'minutes',
                    minValue: moment.duration(followupMinAdditionalTime, 'minutes'),
                }),
            ),
        );

        // For  short confirmation, set appointment date to next working day and appointment time as received from CODGRP table via ValueHelperService
        // Saud i : does not have value helper and date is +3 advance
        const followUpDispatchTimeValueHelper = valueHelperService.getFollowDispatchTime()[0];

        if (this.configService.showFollowupLongText()) {
            this.followupTextLong = new MultilineTextModel('FollowupText');
            modelOptionsLoader.load(this.followupTextLong, 'ConfirmationFollowup', 'FollowupText');
            this.followupTextLong = assignModelOptions(
                this.followupTextLong,
                {
                    ...this.followupTextLong.options,
                    readonly: true,
                },
                this,
            );
            this.followupTextShort = <MultilineTextModel>this.set(new MultilineTextModel('CrmLongTextSFOLSet'));
        }
        if (this.configService.showFollowupAdditionalInfo()) {
            this.additionalInfo = new MultilineTextModel('AdditionalInfo');
            modelOptionsLoader.load(this.additionalInfo, 'ConfirmationFollowup', 'FollowupText');
        }
        if (this.configService.showFollowupLongText() || this.configService.showFollowupAdditionalInfo()) {
            this.crmLongTextTRTESet = <MultilineTextModel>this.set(new MultilineTextModel('CrmLongTextTRTESet', ''));
        }
        if (this.isMaterialRequestDependent) {
            this.setFollowUpDependency();
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            this.shortCrmFollowupDateTime = followUpDispatchTimeValueHelper
                ? moment(followUpDispatchTimeValueHelper.code, ConfigConstants.formatNumberPatternTime)
                : this.nextWorkingDayDateTime;
            this.setShortConfirmationDependency();
        }
        if (this.configService.getDisplayTrendCodeInFollowup()) {
            this.reasonsCode = notificationUtilities.getEquipmentErrorCodes(notification);
            if (this.reasonsCode) {
                modelOptionsLoader.load(this.reasonsCode, 'Equipment', 'Reason');
            } else if (
                !this.reasonsCode &&
                (this.clientManager.isNgmAA() ||
                    (this.clientManager.isNgmSA() &&
                        notification.notificationType === ConfigConstants.NotificationTypes.Service))
            ) {
                const equipmentReasonsCodes = valueHelperService.getEquipmentReason();
                // no t ree view data coming, falling back to normal reason codes from value helper
                const reasonsCodeEntries = equipmentReasonsCodes.map(
                    (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('CauseCode', vh.code)),
                );
                this.reasonsCodeFallback = new DropdownModel<TextModel>('CauseCode', reasonsCodeEntries);
                modelOptionsLoader.load(this.reasonsCodeFallback, 'Equipment', 'Reason');
            }
            this.assayNumber = new MultilineTextModel('CrmLongTextASSASet');
            modelOptionsLoader.load(this.assayNumber, 'Equipment', 'AssayNumber');
            this.assayNumber = assignModelOptions(
                this.assayNumber,
                {
                    ...this.assayNumber.options,
                    applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
                },
                this,
            );
            this.lotNumber = new MultilineTextModel('CrmLongTextLOTNSet');
            modelOptionsLoader.load(this.lotNumber, 'Equipment', 'LotNumber');
            this.lotNumber = assignModelOptions(
                this.lotNumber,
                {
                    ...this.lotNumber.options,
                    applicationIdentifier: ConfigConstants.ApplicationIdentifier.LotNumber,
                },
                this,
            );
        }

        this.showLegalCheckHandover =
            this.confirmationType === CONFIRMATION_TYPES.NORMAL &&
            this.configService.getLegalCheckHandoverSafetyInformationCode().length > 0 &&
            notificationUtilities.isInstallationNotificationWithIHOT(notification);
        if (this.showLegalCheckHandover) {
            this.notificationLanguage = notification.languageId;
            this.legalCheckHandoverSafetyInformationText =
                this.configService.getLegalCheckHandoverSafetyInformationText();
            const legalCheckHandoverSafetyInformationCode =
                this.configService.getLegalCheckHandoverSafetyInformationCode();
            this.legalCheckHandoverUserDocumentationText =
                this.configService.getLegalCheckHandoverUserDocumentationText();
            const legalCheckHandoverUserDocumentationCode =
                this.configService.getLegalCheckHandoverUserDocumentationCode();
            this.legalCheckHandoverSafetyInformation = new RadioModel<TextModel>(
                legalCheckHandoverSafetyInformationCode,
                optionGroup,
                new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
            );
            this.legalCheckHandoverUserDocumentation = new RadioModel<TextModel>(
                legalCheckHandoverUserDocumentationCode,
                optionGroup,
                new RadioModelOptions({ initialSelection: 2, invalidSelection: 2 }),
            );
        }

        this.enableAppointmentBooking = this.configService.getEnableAppointmentBooking();
        if (this.enableAppointmentBooking) {
            this.setAvailableSlots();
        }
    }

    private updateNextWorkingDayDateTime(): void {
        const defaultStartTime = this.clientManager.isNgmP58()
            ? this.configService.getDefaultFollowUpTimeP58()
            : ConfigConstants.confirmationFollowUpStartTime;
        const defaultFollowUpTime = moment(defaultStartTime, ConfigConstants.formatNumberPatternTime);
        // Set  appointment date to tomorrow - weekday (excluding saturday and sunday)
        this.nextWorkingDayDateTime = TimeUtilities.getNextWorkingDayDateTime(
            defaultFollowUpTime,
            this.configService.getFollowUpDefaultDays(),
        );
    }

    public resetAppointmentDateAndTime(): void {
        this.updateNextWorkingDayDateTime();
        this.appointmentTime = cloneDeep(this.appointmentTime);
        this.appointmentDate = cloneDeep(this.appointmentDate);

        this.appointmentTime.value = this.nextWorkingDayDateTime.clone();
        this.appointmentTime.options = new TimeModelOptions({
            minValue: moment().startOf('minutes'),
        });
        this.appointmentDate.value = this.appointmentTime.value.clone();

        if (this.availableSlotsDropdownModel) {
            this.availableSlotsDropdownModel.value = [];
        }
    }

    public getShortConfirmationDateTime(): moment.Moment {
        return moment()
            .add(1, 'days')
            .hour(this.shortCrmFollowupDateTime.hour())
            .minute(this.shortCrmFollowupDateTime.minute())
            .startOf('minute');
    }

    public setShortConfirmationDependency(): void {
        /**
         * For P58
         * In case of short confirmation the fields KD_DATUM, KD_ZEIT and DISP_REL has to be sent from mobile to SAP. PLAN_STD will not be sent.
         * KD_DATUM = next day.
         * KD_ZEIT = start time defined in CODGRP table.
         */
        this.followupOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueYes);
        this.appointmentDate.value.set(this.getShortConfirmationDateTime().toObject()); // no n eed to set appointmentTime as both date and time values share same moment object
    }

    public hasEquipmentDependencyP58(): boolean {
        return (
            this.clientManager.isNgmP58() &&
            isDefined(this.equipmentStatus?.equipmentAfterFix?.value) &&
            this.equipmentStatus?.equipmentAfterFix?.value !== ConfigConstants.EQUIPMENT_STATUS.WORKING
        );
    }

    public hasEquipmentDependencySA(): boolean {
        return (
            this.clientManager.isNgmSA() &&
            typeof this.equipmentStatus?.timeOfRecoveryDate !== 'undefined' &&
            typeof this.equipmentStatus?.equipmentInArrival !== 'undefined' &&
            !this.equipmentStatus?.timeOfRecoveryDate.value.isValid() &&
            this.equipmentStatus?.equipmentInArrival.value !== ConfigConstants.EQUIPMENT_STATUS.WORKING &&
            this.equipmentStatus?.equipmentInArrival.value !== ConfigConstants.EQUIPMENT_STATUS.APP_WORKING
        );
    }

    public getMaterialDependencies(): MaterialModel[] {
        if (typeof this.materialsList === 'undefined') {
            return [];
        }
        return this.materialsList.filter((material) => {
            // for  P58 and all AA countries if a material is defined as "Left On Notification" a follow-up should be forced
            if (material.consumptionCode?.selectedEntry?.value === CONSUME_CODE.LEFT_ON_NOTIFICATION) {
                return true;
            }
            // for  P58 and ANZ and India if a material is defined as "Not Received" a follow-up should be forced
            // http s://evorait.atlassian.net/browse/SHCNGMAAP2-75(AA)
            if (
                this.clientManager.isNgmAAANZ() ||
                this.clientManager.isNgmAAIndia() ||
                this.clientManager.isNgmP58OrSA()
            ) {
                return material.materialStatus?.isNotReceived;
            }
        });
    }

    public determineFollowupDependency(): void {
        this.followUpText = [];

        const isEquipmentDependentP58 = this.hasEquipmentDependencyP58();

        const isEquipmentDependentSA = this.hasEquipmentDependencySA();

        const materialDependencies = this.getMaterialDependencies();

        if (isEquipmentDependentP58) {
            this.followUpText.push(`System ${this.equipmentStatus?.equipmentAfterFix?.displayName}`);
        }
        if (isEquipmentDependentSA) {
            this.followUpText.push(`System is fully/partially down`);
        }
        if (materialDependencies.length > 0) {
            materialDependencies.forEach((material) =>
                this.followUpText.push(
                    `Material ${material.consumptionCode.selectedEntry.displayName} ${material.materialIdentifier.materialNumber.value} ${material.description.value}`,
                ),
            );
        }

        if (
            isEquipmentDependentP58 ||
            isEquipmentDependentSA ||
            materialDependencies.length > 0 ||
            this.isMaterialRequestDependent
        ) {
            const followUpTextShort = `${ConfigConstants.FOLLOWUP_SHORT_TEXT.FOLLOWUP}${
                isEquipmentDependentP58 || isEquipmentDependentSA ? ConfigConstants.FOLLOWUP_SHORT_TEXT.EQUIPMENT : ''
            }${materialDependencies.length > 0 ? ConfigConstants.FOLLOWUP_SHORT_TEXT.MATERIAL : ''}`;
            this.setFollowUpDependency(this.followUpText.join('\n'), followUpTextShort);
        } else {
            this.resetFollowUpDependency();
        }
    }

    /**
     * This function deals with the dependency of followup
     *
     * @param followUpTextLong
     * @param followupTextShort
     */
    public setFollowUpDependency(followUpTextLong = '', followupTextShort = ''): void {
        this.followupOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueYes);
        this.followupOptions = assignModelOptions(
            this.followupOptions,
            {
                ...this.followupOptions.options,
                readonly: true,
            },
            this,
        );
        this.isForced = true;
        if (this.followupTextLong) {
            this.followupTextLong = new MultilineTextModel(
                this.followupTextLong.name,
                followUpTextLong,
                this.followupTextLong.options,
            );
        }
        if (this.followupTextShort) {
            this.followupTextShort = <MultilineTextModel>(
                this.set(
                    new MultilineTextModel(
                        this.followupTextShort.name,
                        followupTextShort,
                        this.followupTextShort.options,
                    ),
                )
            );
        }
        if (this.additionalInfo) {
            this.additionalInfo = assignModelOptions(
                this.additionalInfo,
                {
                    ...this.additionalInfo.options,
                    required: false,
                },
                this,
            );
        }
    }

    public resetFollowUpDependency(): void {
        // if t he followup value currently is currently not enforced, we do not reset the control since we do not want
        // to o verwrite values entered by the user. We only proceed if dependency was previously enforced by a
        // depe ndency, eg. equipment or material related.
        if (!this.isForced) {
            return;
        }

        this.followUpText = [];
        this.followupOptions.selectByPredicate((entry) => entry.value === ConfigConstants.optionValueNA);
        this.followupOptions = assignModelOptions(
            this.followupOptions,
            {
                ...this.followupOptions.options,
                readonly: false, // re-e nable the control which was disabled by the forced dependency
            },
            this,
        );
        this.isForced = false;
        if (this.followupTextLong) {
            this.followupTextLong = new MultilineTextModel(
                this.followupTextLong.name,
                '',
                this.followupTextLong.options,
            );
        }
        if (this.followupTextShort) {
            this.followupTextShort = <MultilineTextModel>(
                this.set(new MultilineTextModel(this.followupTextShort.name, '', this.followupTextShort.options))
            );
        }
        if (this.additionalInfo) {
            this.additionalInfo = assignModelOptions(
                this.additionalInfo,
                {
                    ...this.additionalInfo.options,
                    required: true,
                },
                this,
            );
        }
    }

    public setAppointmentSlots(slots: OdataSlotIntervalModel[]): void {
        this.appointmentSlots.length = 0;
        if (slots.length > 0) {
            for (const slot of slots) {
                const isUTC = this.configService.getFormatMomentInUTC();
                const startDate = TimeUtilities.momentFromDateAndTime(slot.StartDate, slot.StartTime, isUTC);
                const finishDate = TimeUtilities.momentFromDateAndTime(slot.FinishDate, slot.FinishTime, isUTC);
                for (let date = startDate.clone(); date.isSameOrBefore(finishDate, 'day'); date.add(1, 'days')) {
                    this.createAppointmentSlot(date, startDate, finishDate);
                }
            }
            this.appointmentDate = cloneDeep(this.appointmentDate);
            this.appointmentDate.value = this.appointmentSlots[0].date.clone();
            this.setAvailableSlots();
            this.updateAppointmentTime();
        }
    }

    private createAppointmentSlot(date: moment.Moment, startDate: moment.Moment, finishDate: moment.Moment): void {
        const dateTimeFormat = this.clientManager.getDateTimeFormat();
        const startTime = date.isSame(startDate, 'day') ? startDate.clone() : date.clone().startOf('day');
        const endTime = date.isSame(finishDate, 'day') ? finishDate.clone() : date.clone().endOf('day');
        this.appointmentSlots.push({
            date: startTime.clone(),
            timeRange: {
                title: `${startTime.format(dateTimeFormat)} to ${endTime.format(dateTimeFormat)}`,
                startTime: startTime,
                endTime: endTime,
            },
        });
    }

    public setAvailableSlots(): void {
        const slots = this.appointmentSlots.filter((slot) => slot.date.isSame(this.appointmentDate.value, 'day'));
        const entries: Array<DisplayDecorator<TextModel>> = slots.map(
            (time) =>
                new DisplayDecorator<TextModel>(time.timeRange.title, new TextModel('Slot Time', time.timeRange.title)),
        );
        this.availableSlotsDropdownModel = new DropdownModel<TextModel>(
            'Slot Time',
            entries,
            new DropdownModelOptions({ required: true }),
        );
        if (entries.length > 0) {
            this.availableSlotsDropdownModel.selectByIndex(0);
            this.updateAppointmentTime();
        }
    }

    public updateAppointmentTime(): void {
        const selectedValue = this.availableSlotsDropdownModel.selectedEntry?.value;
        if (!selectedValue) {
            return;
        }
        const range = this.appointmentSlots.find((slot) => slot.timeRange.title === selectedValue)?.timeRange;
        if (!range) return;

        const startTime = moment(range.startTime);
        if (startTime.isSameOrAfter(moment())) {
            const fifteen = 15;
            this.appointmentTime = cloneDeep(this.appointmentTime);
            this.appointmentTime.value = startTime.clone().add(fifteen, 'minute');
            this.appointmentTime.options = new TimeModelOptions({
                ...this.appointmentTime.options,
                minValue: startTime,
                maxValue: moment(range.endTime),
            });
        }
    }

    public getAppointmentPayload(): ODataAppointmentSlotsPayload {
        return {
            NotificationNo: this.notification.identifier.qmNum,
            TaskNumber: this.notification.identifier.maNum,
            Duration: this.additionalTime.getOutModel().FollowUpVisitAddTime,
            StartDate: this.appointmentDate.value.format(this.appointmentDate.options.format),
            FinishDate: this.appointmentDate.value
                .clone()
                .add(this.configService.getAppointmentBookingNumberOfDays(), 'days')
                .format(this.appointmentDate.options.format),
            UserName: this.notification.getContactName(),
            SlotIntervalSet: [],
        };
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     */
    public toJSON(): unknown {
        if (this.confirmationType === CONFIRMATION_TYPES.NORMAL) {
            this.normalCrmAppointmentTime = this.appointmentTime.value;
            this.normalCrmFollowupOptions = this.followupOptions.selectedEntry.model.value;
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            this.shortCrmFollowupOptions = this.followupOptions.selectedEntry.model.value;
        }
        return {
            followupOptions: this.normalCrmFollowupOptions,
            appointmentTime: this.normalCrmAppointmentTime,
            additionalTime: this.additionalTime,
            followUpReasons:
                this.followUpReasons && this.followUpReasons.selectedEntry
                    ? this.followUpReasons.selectedEntry.value
                    : undefined,
            followUpUMRCodes:
                this.followUpUMRCodes && this.followUpUMRCodes.selectedEntry
                    ? this.followUpUMRCodes.selectedEntry.value
                    : undefined,
            isMaterialDependent: this.isForced,
            followupTextLong: this.followupTextLong ? this.followupTextLong.value : undefined,
            followupTextShort: this.followupTextShort ? this.followupTextShort.value : undefined,
            additionalInfo: this.additionalInfo ? this.additionalInfo.value : undefined,
            shortCrmFollowupOptions: this.shortCrmFollowupOptions,
            reasonsCode: this.reasonsCode
                ? this.reasonsCode.selectedEntries.map(
                      (node) => `${node.id}${ConfigConstants.stringSeparator}${node.value}`,
                  )
                : undefined,
            reasonsCodeFallback: this.reasonsCodeFallback
                ? this.reasonsCodeFallback.selectedEntries.map((entry) => entry.value)
                : undefined,
            assayNumber: this.assayNumber ? this.assayNumber.value : undefined,
            lotNumber: this.lotNumber ? this.lotNumber.value : undefined,
            legalCheckHandoverSafetyInformation: this.legalCheckHandoverSafetyInformation
                ? this.legalCheckHandoverSafetyInformation.selectedEntry.value
                : undefined,
            legalCheckHandoverUserDocumentation: this.legalCheckHandoverUserDocumentation
                ? this.legalCheckHandoverUserDocumentation.selectedEntry.value
                : undefined,
            appointmentSlots: this.appointmentSlots?.map((slot) => {
                return { date: slot.date.format(this.clientManager.getDateFormat()), timeRange: slot.timeRange };
            }),
            bookedSlot: this.enableAppointmentBooking
                ? this.availableSlotsDropdownModel.selectedEntry?.displayName
                : undefined,
            enableAppointmentBooking: this.enableAppointmentBooking,
        };
    }

    /**
     * This method is required to avoid overwriting of appointment date and time while switching from
     * short confirmation to normal confirmation and vice versa
     *
     * @returns {ODataConfirmationFollowupOutModel}
     */
    public getOutModel(): ODataConfirmationFollowupOutModel {
        // If there is additional info added by user, Follow-up short text must contain the string "INT"
        if (this.followupTextShort && this.additionalInfo && this.additionalInfo.value.length > 0) {
            if (this.followupTextShort.value.length === 0) {
                this.followupTextShort.value = ConfigConstants.FOLLOWUP_SHORT_TEXT.FOLLOWUP;
            }
            this.followupTextShort.value = this.followupTextShort.value.concat(
                ConfigConstants.FOLLOWUP_SHORT_TEXT.ADD_INFO,
            );
        }
        // If both followup long text and additional info exists, they need to be combined to be sent with CRM
        if (this.followupTextLong?.value.length > 0) {
            this.followupTextLong.value = this.followupTextLong.value.concat('\n');
        }
        if (isDefined(this.crmLongTextTRTESet)) {
            this.crmLongTextTRTESet.value = `${this.followupTextLong ? this.followupTextLong.value : ''}${
                this.additionalInfo ? this.additionalInfo.value : ''
            }`;
        }
        const outModel = <ODataConfirmationFollowupOutModel>super.getOutModel();
        // Saudi needs followup reason and flag, so we will not delete them
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'UmrCode'>).UmrCode;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'FollowUpVisitAddTime'>).FollowUpVisitAddTime;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'CrmLongTextTRTESet'>).CrmLongTextTRTESet;
            delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'CrmLongTextSFOLSet'>).CrmLongTextSFOLSet;

            if (this.clientManager.isNgmP58()) {
                delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'FollowUpVisitFlag'>).FollowUpVisitFlag;
                delete (outModel as Omit<ODataConfirmationFollowupOutModel, 'FollowupReason'>).FollowupReason;
            }
        }

        if (this.configService.getDisplayTrendCodeInFollowup()) {
            // if a reason code was selected, include its error code and error code group. Else send empty strings instead.
            let causeCode = new TextModel('CauseCode').getOutModel();
            let causeCodeGroup = new TextModel('CauseCodeGroup').getOutModel();
            if (this.reasonsCode && this.reasonsCode.selectedEntry) {
                causeCode = this.reasonsCode.selectedEntry.payload.codeName.getOutModel();
                causeCodeGroup = this.reasonsCode.selectedEntry.payload.codeGroup.getOutModel();
            } else if (this.reasonsCodeFallback && this.reasonsCodeFallback.selectedEntry) {
                causeCode = <ODataTextOutModel>this.reasonsCodeFallback.selectedEntry.getOutModel();
            }
            _.merge(outModel, causeCode);
            _.merge(outModel, causeCodeGroup);
            // If t he error code is an assay number we need to transmit assay code and lot nr as well
            if (
                this.reasonsCode &&
                this.reasonsCode.selectedEntry &&
                this.reasonsCode.selectedEntry.payload.codeGroup.value.indexOf(this.assayIndicator) === 0
            ) {
                _.merge(outModel, this.assayNumber.getOutModel());
                _.merge(outModel, this.lotNumber.getOutModel());
            }
        }

        if (this.showLegalCheckHandover) {
            const codeTextForYes = this.notificationLanguage === ConfigConstants.configKeyTurkey ? 'evet' : 'Yes';
            const codeTextForNo = this.notificationLanguage === ConfigConstants.configKeyTurkey ? 'hayır' : 'No';
            const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));
            crmCodes.addModel(
                new CrmCodeModel({
                    codeType: 'M',
                    codeGroup: 'IEC6',
                    code: this.legalCheckHandoverSafetyInformation.name,
                    codeText:
                        this.legalCheckHandoverSafetyInformation.selectedEntry.value === ConfigConstants.optionValueYes
                            ? codeTextForYes
                            : codeTextForNo,
                }),
            );
            crmCodes.addModel(
                new CrmCodeModel({
                    codeType: 'M',
                    codeGroup: 'IEC6',
                    code: this.legalCheckHandoverUserDocumentation.name,
                    codeText:
                        this.legalCheckHandoverUserDocumentation.selectedEntry.value === ConfigConstants.optionValueYes
                            ? codeTextForYes
                            : codeTextForNo,
                }),
            );
            _.merge(outModel, crmCodes.getOutModel());
        }

        if (this.enableAppointmentBooking) {
            outModel.FollowUpVisitDate = this.appointmentDate.value.format(this.appointmentDate.options.format);
            outModel.FollowUpVisitTime = this.appointmentTime.value.format(this.appointmentTime.options.format);
        }

        return outModel;
    }
}

export class ConfirmationFollowupModelFromJson extends ConfirmationFollowupModel {
    public constructor(
        data: JsonConfirmationFollowupModel,
        notification: NotificationModel,
        notificationType: ConfigConstants.NotificationTypes,
        crmType: CONFIRMATION_TYPES,
        isMaterialRequestDependent: boolean,
        equipmentStatus: EquipmentStatus | undefined,
        materialList: MaterialModel[] | undefined,
        equipment: JsonConfirmationEquipmentModel | undefined, // migr ation logic SHP-3884
    ) {
        super(notification, notificationType, crmType, isMaterialRequestDependent, equipmentStatus, materialList);
        if (this.confirmationType === CONFIRMATION_TYPES.NORMAL && data.followupOptions) {
            this.followupOptions.selectByPredicate((entry) => entry.value === data.followupOptions);
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT && data.shortCrmFollowupOptions) {
            this.followupOptions.selectByPredicate((entry) => entry.value === data.shortCrmFollowupOptions);
        }
        this.normalCrmFollowupOptions = data.followupOptions;
        this.shortCrmFollowupOptions = data.shortCrmFollowupOptions;

        let followupTime = moment.invalid();
        if (this.confirmationType === CONFIRMATION_TYPES.NORMAL) {
            followupTime =
                typeof data.appointmentTime !== 'undefined' ? moment(data.appointmentTime) : this.appointmentTime.value;
        }
        if (this.confirmationType === CONFIRMATION_TYPES.SHORT) {
            this.shortCrmAppointmentTime = this.getShortConfirmationDateTime();
            followupTime = this.shortCrmAppointmentTime;
        }
        this.appointmentDate.value = followupTime;
        this.appointmentTime.value = followupTime;

        this.normalCrmAppointmentTime = moment(data.appointmentTime);

        this.additionalTime.value = moment.duration(data.additionalTime.value);
        if (this.followUpReasons && data.followUpReasons) {
            this.followUpReasons.selectByPredicate((entry) => entry.value === data.followUpReasons);
        }
        if (this.followUpUMRCodes && data.followUpUMRCodes) {
            this.followUpUMRCodes.selectByPredicate((entry) => entry.value === data.followUpUMRCodes);
        }
        if (this.followupTextLong && data.followupTextLong) {
            this.followupTextLong.value = data.followupTextLong;
        }
        if (this.followupTextShort && data.followupTextShort) {
            this.followupTextShort.value = data.followupTextShort;
        }
        if (this.additionalInfo && data.additionalInfo) {
            this.additionalInfo.value = data.additionalInfo;
        }
        this.isForced = data.isMaterialDependent;
        if (this.isForced || isMaterialRequestDependent) {
            this.setFollowUpDependency(data.followupTextLong, data.followupTextShort);
        }

        const reasonsCode = data.reasonsCode || equipment?.reasonsCode;
        if (reasonsCode && this.reasonsCode) {
            reasonsCode.forEach((label) => {
                const nodeId = label.split(ConfigConstants.stringSeparator)[0];
                const nodeTitle = label.split(ConfigConstants.stringSeparator)[1];
                if (this.reasonsCode) {
                    this.reasonsCode.selectEntryByIdAndTitle(nodeId, nodeTitle);
                } else {
                    throw new Error(
                        'Could not re-create confirmation followup model from JSON due to missing matching reasonscode entry!',
                    );
                }
            });
        }

        const reasonsCodeFallback = data.reasonsCodeFallback || equipment?.reasonsCodeFallback;
        if (reasonsCodeFallback && this.reasonsCodeFallback) {
            reasonsCodeFallback.forEach((value) => {
                this.reasonsCodeFallback.selectByPredicate((code) => code.value === value);
            });
        }

        const assayNumber = data.assayNumber || equipment?.assayNumber;
        if (assayNumber && this.assayNumber) {
            this.assayNumber.value = assayNumber;
        }

        const lotNumber = data.lotNumber || equipment?.lotNumber;
        if (lotNumber && this.lotNumber) {
            this.lotNumber.value = lotNumber;
        }

        if (this.legalCheckHandoverUserDocumentation && data.legalCheckHandoverUserDocumentation) {
            this.legalCheckHandoverUserDocumentation.selectByPredicate(
                (entry) => entry.value === data.legalCheckHandoverUserDocumentation,
            );
        }

        if (this.legalCheckHandoverSafetyInformation && data.legalCheckHandoverSafetyInformation) {
            this.legalCheckHandoverSafetyInformation.selectByPredicate(
                (entry) => entry.value === data.legalCheckHandoverSafetyInformation,
            );
        }
        if (data.enableAppointmentBooking) {
            this.appointmentSlots = data.appointmentSlots?.map((slot) => {
                return { date: moment(slot.date, this.clientManager.getDateFormat()), timeRange: slot.timeRange };
            });
            this.setAvailableSlots();
            if (data.bookedSlot) {
                this.availableSlotsDropdownModel.selectByDisplayName(data.bookedSlot);
            }
        }
    }
}

export interface JsonConfirmationFollowupModel {
    followupOptions: string;
    appointmentTime: string;
    additionalTime: JsonDurationModel;
    followUpReasons: string;
    followUpUMRCodes: string;
    isMaterialDependent: boolean;
    followupTextLong: string | undefined;
    followupTextShort: string | undefined;
    additionalInfo: string | undefined;
    shortCrmFollowupOptions: string | undefined;
    reasonsCode: string[] | undefined;
    reasonsCodeFallback: string[] | undefined;
    assayNumber: string | undefined;
    lotNumber: string | undefined;
    legalCheckHandoverSafetyInformation: string | undefined;
    legalCheckHandoverUserDocumentation: string | undefined;
    appointmentSlots: AppointmentSlot[];
    bookedSlot: string;
    enableAppointmentBooking: boolean;
}

export interface ODataConfirmationFollowupOutModel extends ODataObject {
    FollowUpVisitFlag: string;
    FollowUpVisitDate: string;
    FollowUpVisitTime: string;
    FollowUpVisitAddTime: string;
    FollowupReason: string;
    UmrCode: string;
    CrmLongTextTRTESet: ODataTextLineOutModel[];
    CrmLongTextSFOLSet: ODataTextLineOutModel[];
    CauseCode: string;
    CauseCodeGroup: string;
    AssayNumber: string;
    LotNumber: string;
    CrmCodesSet: ODataCrmCodeOutModel[];
}
